// MUI
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// Components
import { FormikDatePicker, FormikTextField } from "Common/Components";
import { MBSChip } from "Components/Users/MBS/Common";

// Styles
import { styles } from "./styles";

// Form Fields
import {
  getContactFields,
  getDemographicsFields,
  getHealthCardFields,
  getAccountDetailsFields,
  inSystemRequiredFields,
  guestRequiredFields,
} from "./patientFields";

const getFieldConfig = (field, inSystem, healthCardProvince) => ({
  ...field,
  required:
    field.id === "registrationNumber" && healthCardProvince === "MB"
      ? true
      : inSystem === false
        ? guestRequiredFields.has(field.id)
        : inSystemRequiredFields.has(field.id),
  disabled:
    field.id === "registrationNumber" && healthCardProvince !== "MB"
      ? true
      : field.disabled,
  onChange:
    field.id === "healthCardProvince"
      ? (event, form) => {
          const selectedProvince = event.target.value;
          if (selectedProvince !== "MB") {
            form.setFieldValue("registrationNumber", "");
          }
          form.handleChange(event);
        }
      : field.onChange,
});

const renderField = (formik, field, inSystem) => {
  const resolvedField = getFieldConfig(
    field,
    inSystem,
    formik.values.healthCardProvince
  );

  return resolvedField.type === "date" ? (
    <FormikDatePicker
      key={resolvedField.id}
      formik={formik}
      field={resolvedField}
    />
  ) : (
    <FormikTextField
      key={resolvedField.id}
      name={resolvedField.id}
      formik={formik}
      field={resolvedField}
    />
  );
};

const SectionHeading = ({ title, inSystem }) => (
  <Stack direction="row" spacing={1} alignItems="center" width="100%">
    <Typography sx={styles.title}>{title}</Typography>
    <Divider sx={{ flex: 1 }} />
    {typeof inSystem === "boolean" && <MBSChip inSystem={inSystem} />}
  </Stack>
);

const PatientDetails = ({
  formik,
  inSystem = null,
  bookAppointment,
  isMBS,
}) => {
  const { t } = useTranslation();
  const demographicsFields = getDemographicsFields(t);
  const healthCardFields = getHealthCardFields(t);
  const contactFields = getContactFields(t);
  const accountDetailsFields = getAccountDetailsFields(t);

  return (
    <Stack component="form" onSubmit={formik.handleSubmit} spacing={3} pt={2}>
      <Stack spacing={1.5} direction="row" useFlexGap sx={{ flexWrap: "wrap" }}>
        <SectionHeading
          title={t("patientForm.demographics")}
          inSystem={inSystem}
        />
        {demographicsFields.map((field) =>
          renderField(formik, field, inSystem)
        )}
      </Stack>

      <Stack spacing={1.5} direction="row" useFlexGap sx={{ flexWrap: "wrap" }}>
        <SectionHeading title={t("patientForm.healthCardInformation")} />
        {healthCardFields.map((field) => renderField(formik, field, inSystem))}
      </Stack>

      <Stack spacing={1.5} direction="row" useFlexGap sx={{ flexWrap: "wrap" }}>
        <SectionHeading title={t("patientForm.contactInformation")} />
        {contactFields.map((field) => renderField(formik, field, inSystem))}
      </Stack>

      <Stack spacing={1.5} direction="row" useFlexGap sx={{ flexWrap: "wrap" }}>
        <SectionHeading title={t("patientForm.accountInformation")} />
        {accountDetailsFields.map((field) =>
          renderField(formik, field, inSystem)
        )}
      </Stack>

      <Stack spacing={1}>
        {isMBS && (
          <Button
            variant="contained"
            sx={{ width: "100%" }}
            onClick={bookAppointment}
          >
            {t("buttons.continueToBooking")}
          </Button>
        )}
        <Button
          type="submit"
          variant={isMBS ? "outlined" : "contained"}
          sx={{ width: "100%" }}
          disabled={!formik.dirty}
        >
          {t("patientForm.saveChanges")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default PatientDetails;
